import { IDataPoint, TDataRef } from './data.types'
import { IDataMessage } from '../ws/ws.types.ts'
import { IComputedParameterDef, IParameter, TParameterKey } from '@/pages/Parameters/parameters.types.ts'
import { RefObject } from 'react'
import { IEquipment } from '@/pages/Equipment/equipment.types.ts'

const transformDataMessage = (
  msg: IDataMessage,
  parametersMap: Record<IParameter['sourceId'], IParameter>
): IDataPoint => {
  return {
    ts: Math.round(msg.timestampNano / 1000000),
    equipmentId: +msg.deviceId,
    values: msg.values.map((v) => ({
      value: v.value === undefined ? 0 : v.value, // Protobuf обрезает false значения
      parameterId: parametersMap[v.name]?.id,
    })),
  }
}

/**
 * Получить отрезок времени между [tsFrom, tsTo], за который данные еще не были получены
 * */
const normalizeSegmentTimestamps = (tsFrom: number, tsTo: number, existingPoints: IDataPoint[]) => {
  // Нет ни одной точки
  if (!existingPoints?.length) return { tsFrom, tsTo }

  // Правая граница дальше всех существующих точек - необходимо обновить все данные
  if (tsTo > existingPoints[existingPoints.length - 1].ts) {
    return { tsFrom, tsTo }
  }

  // Все точки уже есть
  if (existingPoints[0].ts <= tsFrom && existingPoints[existingPoints.length - 1].ts >= tsTo) {
    return null
  }

  // Не хватает точек по левой границе
  if (existingPoints[0].ts < tsFrom && existingPoints[existingPoints.length - 1].ts >= tsTo) {
    return { tsFrom, tsTo: existingPoints[0].ts - 1 }
  }

  return { tsFrom, tsTo }
}

/**
 * Удалить уже отображенные и более неиспользуемые точки
 * */
const clearUsedPoints = (dataRef: RefObject<TDataRef>, initialTsFrom: number, initialTsTo: number) => {
  const timeDif = Date.now() - initialTsTo
  const currentFrom = initialTsFrom + timeDif

  for (const deviceId in dataRef.current) {
    let removeCount = 0
    for (const point of dataRef.current[deviceId]) {
      if (currentFrom > point.ts) removeCount++
      else break
    }

    dataRef.current[deviceId].splice(0, removeCount)
  }
}

const clearDiscredetationErrors = (
  points: Array<{ ts: number; value: number }>
): Array<{ ts: number; value: number }> => {
  const newPoints: Array<{ ts: number; value: number }> = []
  let maxSecValue = -Number.MAX_SAFE_INTEGER
  let minSecValue = Number.MAX_SAFE_INTEGER

  for (let i = 0; i < points.length; ++i) {
    if (points[i].value > maxSecValue) {
      maxSecValue = points[i].value
    }
    if (points[i].value < minSecValue) {
      minSecValue = points[i].value
    }
  }

  const maxReasonableDelta = (maxSecValue - minSecValue) * 0.4

  let lastPositiveValue
  for (let i = 0; i < points.length; ++i) {
    if (points[i].value > 0) {
      lastPositiveValue = points[i].value
    }

    if (lastPositiveValue && points[i].value === 0 && lastPositiveValue > maxReasonableDelta) {
      continue
    }

    newPoints.push(points[i])
  }

  return newPoints
}

interface IFillDataPointParams {
  parameters: IParameter['id'][]
  point: IDataPoint
  lastValuesRef: RefObject<Record<IEquipment['id'], Record<IParameter['id'], { ts: number; value: number }>>>
  equipmentId: IEquipment['id']
}
const fillDataPointWithRequiredLastValues = (params: IFillDataPointParams): IDataPoint => {
  const point: IDataPoint = { ts: params.point.ts, equipmentId: params.point.equipmentId, values: [] }
  const existingParametersSet = new Set<IParameter['id']>()

  for (const value of params.point.values) {
    existingParametersSet.add(value.parameterId)
    point.values.push(value)
  }

  for (const parameterId of params.parameters) {
    if (existingParametersSet.has(parameterId)) continue

    if (params.lastValuesRef.current[params.equipmentId]) {
      point.values.push({
        parameterId,
        value: params.lastValuesRef.current[params.equipmentId][parameterId]?.value || 0,
      })
    }
  }

  return point
}

const parsedToBaseParameterIds = (
  parameters: TParameterKey[],
  computed: IComputedParameterDef[] = []
): IParameter['id'][] => {
  const filteredParams = parameters.filter((p) => typeof p === 'number')
  const requiredCalcParam = computed.map((c) => c.dependsOn).flat()
  return Array.from(new Set([...filteredParams, ...requiredCalcParam]))
}

const dataUtils = {
  transformDataMessage,
  normalizeSegmentTimestamps,
  clearUsedPoints,
  clearDiscredetationErrors,
  fillDataPointWithRequiredLastValues,
  parsedToBaseParameterIds,
}
export default dataUtils
